import React, { useContext } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';
import ProjectContext from '../context/ProjectContext';
import AuthContext from '../context/AuthContext';

// Components
import BalanceRibbon from '../components/reconcile/BalanceRibbon';
import ReconcileTable from '../components/reconcile/ReconcileTable';
import ReconcileSetup from '../components/reconcile/ReconcileSetup';
import ReconciliationDashboard from '../components/reconcile/ReconciliationDashboard';
import ReconcileComparisonView from '../components/reconcile/ReconcileComparisonView';
import StatementSummaryModal from '../components/reconcile/StatementSummaryModal';
import { ToggleButtonGroup, ToggleButton } from 'react-bootstrap';

// Hooks
import { useReconcile } from '../hooks/useReconcile';

const Reconcile = () => {
  const { activeProject } = useContext(ProjectContext);
  const { user } = useContext(AuthContext);
  const [showOnlyCurrentMonth, setShowOnlyCurrentMonth] = React.useState(true);
  const [showDiffOnly, setShowDiffOnly] = React.useState(false);
  const [viewMode, setViewMode] = React.useState('selection');
  
  const {
    accounts, selectedAccount, setSelectedAccount,
    selectedYear, setSelectedYear,
    reconcileData, setReconcileData,
    dbTransactions, selectedTxIds, toggleTransaction,
    loading, error, step, setStep,
    uploadStatement, loadExistingDocument, fetchDbTransactions, finalizeReconcile,
    stats, recentDocuments, savedStatements,
    monthlyStatus, startMonthlyReconcile,
    pdfTransactions,
    summaryTargets, showSummaryModal, setShowSummaryModal
  } = useReconcile(activeProject, user);

  if (!activeProject) return <div className="p-5 text-center">Please select a company first.</div>;

  const handleConfirmSummary = () => {
    setShowSummaryModal(false);
    fetchDbTransactions();
  };

  return (
    <div className="pb-5">
      <StatementSummaryModal 
        show={showSummaryModal}
        onHide={() => setShowSummaryModal(false)}
        onConfirm={handleConfirmSummary}
        summaryTargets={summaryTargets}
        pdfTransactions={pdfTransactions}
        reconcileData={reconcileData}
      />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          {step > 0 && (
            <Button variant="link" className="p-0 mr-3 text-dark" onClick={() => setStep(0)} title="Back to Months">
              <i className="fas fa-arrow-left fa-lg"></i>
            </Button>
          )}
          <h1 className="mb-0">Reconcile Account</h1>
        </div>
        <Badge variant="primary" className="px-3 py-2 shadow-sm">{activeProject.name}</Badge>
      </div>

      {step === 0 && (
        <ReconciliationDashboard 
          accounts={accounts}
          selectedAccount={selectedAccount}
          setSelectedAccount={setSelectedAccount}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          monthlyStatus={monthlyStatus}
          onStart={startMonthlyReconcile}
        />
      )}

      {step === 1 && (
        <ReconcileSetup 
          selectedAccount={selectedAccount}
          setSelectedAccount={setSelectedAccount}
          accounts={accounts}
          onFileUpload={(e) => e.target.files[0] && uploadStatement(e.target.files[0])}
          reconcileData={reconcileData}
          setReconcileData={setReconcileData}
          recentDocuments={recentDocuments}
          savedStatements={savedStatements}
          loadExistingDocument={loadExistingDocument}
          fetchDbTransactions={fetchDbTransactions}
          loading={loading}
          error={error}
        />
      )}

      {step === 2 && (
        <div className="reconcile-matching-view">
          <BalanceRibbon 
            stats={stats} 
            endingBalance={reconcileData.endingBalance}
            onFinish={finalizeReconcile}
            onBack={() => setStep(1)}
            loading={loading}
          />

          <div className="alert alert-info py-2 small d-flex justify-content-between align-items-center">
             <span><i className="fas fa-info-circle mr-2"></i> Only transactions on or before <strong>{new Date(reconcileData.statementDate).toLocaleDateString()}</strong> are shown below.</span>
             <Button variant="link" size="sm" onClick={() => setStep(1)} className="p-0">Edit Cutoff Date</Button>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center">
                <h5 className="mb-0 text-muted small text-uppercase font-weight-bold mr-3">Match Statement Transactions</h5>
                <ToggleButtonGroup type="radio" name="viewMode" value={viewMode} onChange={v => setViewMode(v)} size="sm">
                    <ToggleButton id="tbg-selection" value="selection" variant="outline-info">
                        Selection View
                    </ToggleButton>
                    <ToggleButton id="tbg-investigation" value="investigation" variant="outline-info">
                        Investigation View
                    </ToggleButton>
                </ToggleButtonGroup>
                {viewMode === 'investigation' && (
                  <Form.Check 
                    type="switch"
                    id="diff-only-switch"
                    label="Show differences only"
                    className="small font-weight-bold text-danger ml-3"
                    checked={showDiffOnly}
                    onChange={e => setShowDiffOnly(e.target.checked)}
                  />
                )}
            </div>
            <Form.Check 
              type="switch"
              id="month-filter-switch"
              label="Show only current month"
              className="small font-weight-bold text-muted"
              checked={showOnlyCurrentMonth}
              onChange={e => setShowOnlyCurrentMonth(e.target.checked)}
            />
          </div>

          {viewMode === 'selection' ? (
            <ReconcileTable 
                transactions={(() => {
                if (!showOnlyCurrentMonth || !reconcileData.statementDate) return dbTransactions;
                const stmtDate = new Date(reconcileData.statementDate);
                const month = stmtDate.getUTCMonth();
                const year = stmtDate.getUTCFullYear();
                return dbTransactions.filter(t => {
                    const d = new Date(t.date);
                    return d.getUTCMonth() === month && d.getUTCFullYear() === year;
                });
                })()} 
                selectedTxIds={selectedTxIds} 
                onToggle={toggleTransaction}
                selectedAccount={selectedAccount}
            />
          ) : (
            <ReconcileComparisonView 
                dbTransactions={(() => {
                    if (!showOnlyCurrentMonth || !reconcileData.statementDate) return dbTransactions;
                    const stmtDate = new Date(reconcileData.statementDate);
                    const month = stmtDate.getUTCMonth();
                    const year = stmtDate.getUTCFullYear();
                    return dbTransactions.filter(t => {
                        const d = new Date(t.date);
                        return d.getUTCMonth() === month && d.getUTCFullYear() === year;
                    });
                })()}
                pdfTransactions={pdfTransactions}
                selectedTxIds={selectedTxIds}
                onToggle={toggleTransaction}
                selectedAccount={selectedAccount}
                showDiffOnly={showDiffOnly}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Reconcile;
